import REST_CLIENT from './restClient';
import Logger, { LOG_LEVELS } from './logger';
import {
    DIRECT_SUBMISSION_ROLES,
    DEFAULT_CONDITION,
    WILDCARD,
    ALL_COMPANIES_GRANT,
} from '../components/directSubmissionPermissions/constants';
import { parseConditionScopes } from './directSubmissionAccess';

const log = new Logger('DS_GRANT_RECONCILE', LOG_LEVELS.DEBUG);

/**
 * Parse a DS grant condition into company/country pairs (via the shared parseConditionScopes) plus isDefault.
 */
export const parseDsCondition = (condition) => {
    const pairs = parseConditionScopes(condition);
    const isDefault = pairs.length === 1 && pairs[0].companyId === WILDCARD && pairs[0].countryCode === WILDCARD;
    return { isDefault, pairs };
};

/**
 * Rebuild a condition string from company/country pairs. Mirrors the parenthesized clause format
 * produced by the modal's builder so a no-op reconcile is byte-identical (idempotency).
 */
export const buildConditionFromPairs = (pairs) => {
    const clauses = (pairs || [])
        .map(pair => `(companyId EQ '${pair.companyId}' AND countryCode EQ '${pair.countryCode}')`);
    return clauses.length ? clauses.join(' OR ') : DEFAULT_CONDITION;
};

/** Full-body PUT payload for updating a grant's condition (only the condition changes). */
export const buildGrantUpdateBody = (existingGrant, condition) => {
    const permissions = existingGrant.rules?.[0]?.permissions
        || existingGrant.resources?.[0]?.permissions
        || [];
    return {
        type: 'custom',
        tenant: { identifier: existingGrant.tenant.identifier },
        system: { identifier: existingGrant.system.identifier },
        role: { identifier: existingGrant.role.identifier },
        source: { identifier: existingGrant.source.identifier },
        displayName: existingGrant.displayName,
        description: existingGrant.description,
        rules: [{ permissions, condition }],
        resources: existingGrant.resources,
    };
};

/** Fetch a user's custom DS grants keyed by role. */
export const getUserDsGrants = async (iamTenantId, iamUserId) => {
    const response = await REST_CLIENT.getIamUserGrants(iamTenantId, iamUserId);
    const items = response?.items || [];
    const findByRole = (roleName) => items.find(g => g?.role?.displayName === roleName && g.type === 'custom');
    return {
        initiator: findByRole(DIRECT_SUBMISSION_ROLES.INITIATOR),
        approver: findByRole(DIRECT_SUBMISSION_ROLES.APPROVER),
    };
};


// Decide what to do with one DS grant given the user's live companyGrants:
//   'none'   - no drift: default/_all_ scope, all-companies access, or already a subset
//   'narrow' - some granted companies survive -> rewrite the condition to that subset
//   'revoke' - no granted company survives -> remove the role
export const computeReconcileAction = (existingCondition, companyGrants) => {
    const grants = companyGrants || [];
    if (grants.includes(ALL_COMPANIES_GRANT)) {
        return { action: 'none' };
    }
    const { isDefault, pairs } = parseDsCondition(existingCondition);
    if (isDefault || pairs.length === 0) {
        return { action: 'none' };
    }
    const allowed = new Set(grants.map(String));
    const retained = pairs.filter(pair => pair.companyId === WILDCARD || allowed.has(pair.companyId));
    if (retained.length === 0) {
        return { action: 'revoke' };
    }
    const condition = buildConditionFromPairs(retained);
    return condition === existingCondition ? { action: 'none' } : { action: 'narrow', condition };
};


// Reconcile one user's DS grants against their live companyGrants.
export const reconcileUserDsGrants = async ({ iamTenantId, iamUserId, companyGrants }) => {
    if (!iamTenantId || !iamUserId) {
        return null;
    }
    try {
        const grants = await getUserDsGrants(iamTenantId, iamUserId);
        let changed = false;
        for (const role of ['initiator', 'approver']) {
            const grant = grants[role];
            if (!grant) {
                continue;
            }
            const current = grant.rules?.[0]?.condition;
            if (current === undefined || current === null) {
                continue;
            }
            const { action, condition } = computeReconcileAction(current, companyGrants);
            if (action === 'narrow') {
                await REST_CLIENT.updateIamGrant(grant.id, buildGrantUpdateBody(grant, condition));
                changed = true;
            } else if (action === 'revoke') {
                await REST_CLIENT.removeGrantFromIamUser(iamTenantId, iamUserId, grant.id);
                changed = true;
            }
        }
        return { changed };
    } catch (error) {
        log.warn('Direct Submission grant reconcile skipped for user', iamUserId, error);
        return null;
    }
};
